/* eslint-disable @typescript-eslint/no-explicit-any */
/** Projects list + tabbed detail + timeline editor + add/edit modals. Ported from `projects.jsx`. */
import { useState } from 'react';
import { useData, useDataActions } from '../context/AppDataContext';
import { useProfileViewer } from '../components/ProfileViewer';
import { useCan } from '../context/AuthContext';
import {
  Ico, Avatar, UserBy, CategoryChip, Modal, STATUS_META,
  fmtShortDate, taskDates, monthFromIso, HERO_YEAR, useToast, RowMenu, useRowDrag,
} from '../components/ui';
import { TeamTab, DocumentsTab, MeetingsTab } from './OtherViews';
import { QuotationTab } from './Quotation';
import { CompensationTab } from './Compensation';
import { downloadCsv, dateStamp } from '../lib/exportUtils';
import { apiClient } from '../lib/apiClient';

/** Project-status vocabulary — the manual-OVERRIDE choices. The status itself
 *  is auto-derived from the timeline by today's date (server-side sync); these
 *  are only offered as an explicit pin for decisions the timeline can't know.
 *  'Draft' / 'Quotation preparation' are deliberately absent — those states
 *  belong to the provisional lifecycle ("Step back to draft"), not a pin. */
export const PROJECT_STATUS_OPTIONS = ['Protocol development', 'Ethics submission', 'Ethics approved', 'CRF / eCRF development', 'Recruitment ongoing', 'Follow-up ongoing', 'Data cleaning', 'Analysis ongoing', 'Report writing', 'Completed', 'On hold', 'Terminated'];

export function ProjectsList({ openProject, onNewProject }: { openProject: (id: string, tab?: string) => void; onNewProject: () => void }) {
  const D = useData();
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const projects = D.projects.filter((p) =>
    (statusFilter === 'all' || p.status === statusFilter)
    && (q === '' || p.title.toLowerCase().includes(q.toLowerCase()) || p.code.toLowerCase().includes(q.toLowerCase())));

  const statusToTone = (s: string) => {
    if (s.includes('Recruitment')) return 'green';
    if (s.includes('Analysis')) return 'amber';
    if (s.includes('Protocol')) return 'violet';
    if (s.includes('Quotation')) return 'slate';
    if (s.includes('Follow-up')) return 'cyan';
    if (s.includes('Completed')) return 'green';
    return 'slate';
  };

  return (
    <div className="page-pad fadein">
      <div className="page-head">
        <div>
          <h2>Projects</h2>
          <div className="sub">{D.projects.length} active research projects across the unit</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => {
            if (!projects.length) return;
            downloadCsv(projects, {
              Code: (p: any) => p.code,
              Title: (p: any) => p.shortTitle,
              Status: (p: any) => p.status,
              Category: (p: any) => p.category ?? '',
            }, `projects-${dateStamp()}.csv`);
          }}><Ico.dl /> Export</button>
          <button className="btn primary" onClick={onNewProject}><Ico.plus /> New project</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ padding: 10, display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
            <span style={{ position: 'absolute', left: 8, top: 6.5, color: 'var(--text-3)' }}><Ico.search /></span>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by code or title…" style={{ paddingLeft: 28 }} />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 'auto' }}>
            <option value="all">All statuses</option>
            {[...new Set(D.projects.map((p) => p.status))].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{projects.length} shown</span>
        </div>
        <div className="proj-list">
          <div className="project-row" style={{ background: 'var(--surface-alt)', cursor: 'default', fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            <div>Code</div><div>Title</div><div>Status</div><div>Project Manager</div><div>Timeline %</div><div className="num">Active now</div>
          </div>
          {projects.map((p) => {
            const todayIso = new Date().toISOString().slice(0, 10);
            const lead = D.team.find((u) => u.id === p.pi);
            const tasks = D.timeline.filter((t) => t.project === p.id && (t as any).rowType !== 'phase');
            const done = tasks.filter((t) => t.status === 'completed').length;
            // "Active now" = phases whose REAL date window covers today (timeline-
            // driven, same as the project detail) — not the manual in-progress flag.
            const active = tasks.filter((t) => { const td = taskDates(t); return t.status !== 'completed' && td.startDate <= todayIso && todayIso <= td.endDate; }).length;
            const pctNum = tasks.length ? (done / tasks.length * 100) : 0;
            const pct = pctNum.toFixed(2);
            return (
              <div key={p.id} className="project-row" onClick={() => openProject(p.id)}>
                <div className="pcode mono">{p.code}</div>
                <div className="ptitle">
                  <span className="t" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {p.shortTitle}
                    {p.provisional && <span className="chip" data-tone="amber" style={{ fontSize: 9.5, padding: '1px 6px' }}><span className="dot" />Provisional · awaiting acceptance</span>}
                  </span>
                  <span className="st" title={p.title}>{p.title.length > 80 ? p.title.slice(0, 80) + '…' : p.title}</span>
                </div>
                <div><span className="chip" data-tone={statusToTone(p.status)}><span className="dot" />{p.status}</span></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <Avatar user={lead} size="sm" /> {lead?.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} title={`${done}/${tasks.length} phases completed`}>
                  <div className="bar-track" style={{ flex: 1 }}>
                    <div className="bar-fill" style={{ width: pct + '%', background: pctNum >= 100 ? '#4a8a55' : 'var(--navy)' }} />
                  </div>
                  <span className="mono tabular" style={{ fontSize: 11, color: 'var(--text-2)', width: 50, textAlign: 'right' }}>{pct}%</span>
                </div>
                <div className="num tabular">{active}</div>
              </div>
            );
          })}
        </div>
      </div>

      <RecycleBin />
    </div>
  );
}

/** Recycle bin — soft-deleted projects, restorable for 30 days (delete-capable
 *  roles only). Loads on expand; restoring reloads the app data so the project
 *  and all its collections come back. */
function RecycleBin() {
  const { restoreProject } = useDataActions();
  const toast = useToast();
  const canDelete = useCan('proj.delete') === 'allow';
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState('');
  if (!canDelete) return null;

  const load = async () => {
    try { setRows(await apiClient.projects.listDeleted()); }
    catch { setRows([]); }
  };
  const daysLeft = (iso?: string) => {
    if (!iso) return 0;
    return Math.max(0, 30 - Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  };

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-h" style={{ cursor: 'pointer' }} onClick={() => { setOpen((o) => !o); if (!open && rows === null) void load(); }}>
        <h3><span style={{ display: 'inline-block', width: 14, color: 'var(--text-3)' }}>{open ? '▾' : '▸'}</span>🗑 Recycle bin</h3>
        <span className="sub">Deleted projects are kept 30 days and can be restored</span>
      </div>
      {open && (
        <div className="card-b flush">
          {rows === null ? (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-2)' }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--text-2)' }}>The recycle bin is empty.</div>
          ) : (
            <table>
              <thead><tr><th>Code</th><th>Title</th><th>Deleted</th><th>Recovery window</th><th></th></tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id}>
                    <td className="mono" style={{ fontWeight: 600 }}>{p.code}</td>
                    <td style={{ fontSize: 12 }}>{p.shortTitle || p.title}</td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--text-2)' }}>{p.deletedAt ? p.deletedAt.slice(0, 16).replace('T', ' ') : '—'}</td>
                    <td><span className="chip" data-tone={daysLeft(p.deletedAt) <= 5 ? 'red' : 'amber'}><span className="dot" />{daysLeft(p.deletedAt)} day{daysLeft(p.deletedAt) === 1 ? '' : 's'} left</span></td>
                    <td>
                      <button className="btn sm" disabled={busy === p.id} onClick={async () => {
                        setBusy(p.id);
                        try {
                          await restoreProject(p.id);
                          setRows((rs) => (rs || []).filter((r) => r.id !== p.id));
                          toast(`${p.code} restored`);
                        } catch { /* error toast comes from the context sink */ }
                        finally { setBusy(''); }
                      }}>↩ Restore</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export function ProjectDetail({ projectId, initialTab, backToList, editTask, openQuotation, onEditInBuilder, addTimelineItem, updateTimelineStatus, deleteTimeline, updateComp, toggleReceived, currentUser }: any) {
  const D = useData();
  const p: any = D.projects.find((x) => x.id === projectId);
  const mapInitial = (t: string) => {
    if (t === 'overview' || t === 'design') return 'summary';
    if (t === 'quotation') return 'settings';
    if (t === 'timeline') return 'team';
    return t || 'summary';
  };
  const [tab, setTab] = useState(mapInitial(initialTab));
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = useCan('proj.edit') === 'allow';
  const canDelete = useCan('proj.delete') === 'allow';
  const { revertToDraft, deleteProject } = useDataActions();
  const detailToast = useToast();
  if (!p) return <div className="page-pad">Project not found</div>;

  const tabs = [
    { id: 'summary', label: 'Project Summary' },
    { id: 'settings', label: 'Settings & Quotations' },
    { id: 'team', label: 'Team & Task allocation' },
    { id: 'compensation', label: 'Compensation' },
    { id: 'meetings', label: 'Meetings & Logs' },
    { id: 'documents', label: 'Documents' },
  ];

  return (
    <div className="page-pad fadein">
      <div style={{ marginBottom: 4 }}>
        <button className="btn ghost sm" onClick={backToList}><Ico.chevLeft /> Back to projects</button>
      </div>
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="mono" style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 700, letterSpacing: '0.02em' }}>{p.code}</span>
            <span style={{ color: 'var(--text-3)' }}>·</span>
            <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{p.shortTitle}</span>
            <span className="chip" data-tone="blue"><span className="dot" />{p.status}</span>
          </div>
          <h2 style={{ marginTop: 6, maxWidth: 980 }}>{p.title}</h2>
          <div className="sub" style={{ display: 'flex', gap: 14, marginTop: 6 }}>
            <span>{p.design}</span>
            <span>·</span>
            <span>{p.area}</span>
            <span>·</span>
            <span>Sponsor: {p.sponsor}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <RowMenu label="More actions" items={[
            { label: 'Edit details only (quick)', onClick: () => setEditOpen(true), disabled: !canEdit },
            { label: 'Open Settings & Quotations', onClick: () => setTab('settings') },
            { label: 'View activity log', onClick: () => setTab('summary') },
            {
              label: 'Step back to draft mode…',
              disabled: !canEdit || p.provisional,
              onClick: () => {
                if (!window.confirm(`Move ${p.code} back to DRAFT (provisional)?\n\nIt returns to "awaiting sponsor acceptance" — nothing is deleted, and you re-activate it from the builder once acceptance is recorded again.`)) return;
                void revertToDraft(p.id).then(() => detailToast(`${p.code} moved back to draft — continue it from the builder`)).catch(() => undefined);
              },
            },
            {
              label: 'Delete project (kept 30 days)…',
              disabled: !canDelete,
              onClick: () => {
                if (!window.confirm(`Delete ${p.code} — ${p.shortTitle || p.title}?\n\nIt moves to the recycle bin and can be RESTORED for 30 days (Projects page → Recycle bin). After 30 days it is permanently removed.`)) return;
                void deleteProject(p.id).then(() => { detailToast(`${p.code} moved to the recycle bin (30-day recovery)`); backToList(); }).catch(() => undefined);
              },
            },
          ]} />
          <button
            className="btn primary"
            disabled={!canEdit}
            title={canEdit ? 'Edit ALL steps of this project in the builder — every change is logged with who & when' : 'You do not have permission to edit this project (requires Project Manager / PI / Admin).'}
            onClick={() => onEditInBuilder(p.id)}
          ><Ico.edit /> Edit project</button>
        </div>
      </div>

      <div className="tabs">
        {tabs.map((t) => (
          <button key={t.id} className={'tab' + (tab === t.id ? ' active' : '')} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {editOpen && <ProjectEditModal p={p} onClose={() => setEditOpen(false)} />}
      {tab === 'summary' && <ProjectSummary p={p} />}
      {tab === 'settings' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <ProjectSettings p={p} />
          <QuotationTab p={p} openQuotation={openQuotation} />
        </div>
      )}
      {tab === 'team' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <TeamTab p={p} />
          <TimelineEditor p={p} editTask={editTask} addTimelineItem={addTimelineItem} updateTimelineStatus={updateTimelineStatus} deleteTimeline={deleteTimeline} />
        </div>
      )}
      {tab === 'compensation' && <CompensationTab p={p} updateComp={updateComp} toggleReceived={toggleReceived} currentUser={currentUser} />}
      {tab === 'documents' && <DocumentsTab projectId={p.id} />}
      {tab === 'meetings' && <MeetingsTab projectId={p.id} />}
    </div>
  );
}

function TimelineEditor({ p, editTask, addTimelineItem, updateTimelineStatus, deleteTimeline }: any) {
  const D = useData();
  const items = D.timeline.filter((t) => t.project === p.id);
  const [addOpen, setAddOpen] = useState(false);
  const toast = useToast();

  // Multi-year Gantt: build month columns spanning the ACTUAL task date range (not
  // just 2026), so the timeline scrolls horizontally across as many years as needed.
  const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthKey = (iso: string) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? NaN : d.getFullYear() * 12 + d.getMonth(); };
  const taskKeys = (t: any) => {
    const td = taskDates(t); const base = HERO_YEAR * 12;
    const s = monthKey(td.startDate); const e = monthKey(td.endDate);
    return { sK: Number.isNaN(s) ? base + (t.start || 0) : s, eK: Number.isNaN(e) ? base + (t.end || 0) : e };
  };
  // Phase-divider rows carry no real dates — keep them out of the Gantt month-range math.
  const keyed = items.filter((t: any) => t.rowType !== 'phase').map((t) => taskKeys(t));
  const minK = keyed.length ? Math.min(...keyed.map((x) => x.sK)) : HERO_YEAR * 12;
  let maxK = keyed.length ? Math.max(...keyed.map((x) => x.eK)) : HERO_YEAR * 12 + 11;
  if (maxK - minK > 71) maxK = minK + 71; // cap displayed range at 6 years
  const todayK = (() => { const n = new Date(); return n.getFullYear() * 12 + n.getMonth(); })();
  const cols: number[] = []; for (let k = minK; k <= maxK; k++) cols.push(k);
  const yearGroups: { year: number; span: number }[] = [];
  cols.forEach((k) => { const y = Math.floor(k / 12); const last = yearGroups[yearGroups.length - 1]; if (last && last.year === y) last.span++; else yearGroups.push({ year: y, span: 1 }); });
  const exportCsv = () => downloadCsv(items.filter((t: any) => t.rowType !== 'phase') as any[], {
    Phase: (t: any) => t.name,
    Category: (t: any) => t.category,
    Responsible: (t: any) => (t.splits && t.splits.length ? t.splits.map((s: any) => `${D.team.find((u) => u.id === s.user)?.name || s.user} ${s.pct}%`).join('; ') : (D.team.find((u) => u.id === t.user)?.name || '')),
    Status: (t: any) => t.status,
    Start: (t: any) => taskDates(t).startDate,
    End: (t: any) => taskDates(t).endDate,
  }, `${p.code}-timeline-${dateStamp()}.csv`);

  // Mouse drag-to-reorder rows (same hook as the builder). Persists the new `order`.
  const { reorderTimeline } = useDataActions();
  const { rowProps: tlRowProps, gripProps: tlGripProps } = useRowDrag(items, (next: any[]) => reorderTimeline(next.map((t: any) => t.id)));

  return (
    <div className="card">
      <div className="card-h">
        <div>
          <h3>Timeline & Phase planning</h3>
          <div className="sub">Click a row to edit its dates, people &amp; % split (changes are logged). The chart spans every year your tasks cover — scroll → for later years.</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn sm" onClick={exportCsv} disabled={!items.length}><Ico.dl /> Export</button>
          <button className="btn primary sm" onClick={() => setAddOpen(true)}><Ico.plus /> Add phase / task</button>
        </div>
      </div>
      <div className="card-b flush">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={{ width: 30 }}>#</th>
                <th>Phase / task</th>
                <th>Category</th>
                <th>Responsible</th>
                <th>Status</th>
                {yearGroups.map((g) => <th key={g.year} colSpan={g.span} style={{ textAlign: 'center', borderLeft: '1px solid var(--border)' }}>{g.year}</th>)}
                <th style={{ width: 30 }}></th>
              </tr>
              <tr>
                <th></th><th></th><th></th><th></th><th></th>
                {cols.map((k) => <th key={k} style={{ textAlign: 'center', minWidth: 30, padding: '4px 0', fontSize: 10, ...(k === todayK ? { background: 'var(--surface-alt)', color: 'var(--accent)', fontWeight: 700, borderTop: '2px solid var(--accent)' } : {}) }} title={k === todayK ? 'Current month' : undefined}>{MONTH_ABBR[k % 12]}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((t, i) => {
                if ((t as any).rowType === 'phase') {
                  return (
                    <tr key={t.id} {...tlRowProps(i)} style={{ background: 'var(--surface-alt)' }}>
                      <td className="mono" style={{ color: 'var(--text-3)' }}><span {...tlGripProps(i)} onClick={(e) => e.stopPropagation()}>⠿</span></td>
                      <td colSpan={cols.length + 4} style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11, color: 'var(--text-2)' }}>
                        ▸ {t.name}
                      </td>
                      <td><RowMenu items={[
                        { label: 'Rename divider', onClick: () => editTask(t) },
                        { label: 'Delete divider', danger: true, onClick: () => { if (window.confirm(`Delete phase divider "${t.name}"?`)) { deleteTimeline(t.id); toast('Divider deleted'); } } },
                      ]} /></td>
                    </tr>
                  );
                }
                const tone = (D.categories[t.category] || { tone: 'slate' }).tone;
                return (
                  <tr key={t.id} className="hover-row" onClick={() => editTask(t)} {...tlRowProps(i)}>
                    <td className="mono" style={{ color: 'var(--text-3)' }}><span {...tlGripProps(i)} onClick={(e) => e.stopPropagation()}>⠿</span> {i + 1}</td>
                    <td style={{ fontWeight: 500 }}>
                      {t.name}
                      <div style={{ fontSize: 10.5, color: 'var(--text-2)', marginTop: 2 }} className="mono">
                        {fmtShortDate(taskDates(t).startDate)} → {fmtShortDate(taskDates(t).endDate)}
                      </div>
                    </td>
                    <td><CategoryChip category={t.category} /></td>
                    <td><ResponsibleCell t={t} /></td>
                    <td>
                      <select
                        value={t.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => { updateTimelineStatus(t.id, e.target.value); toast('Status updated · dashboard refreshed'); }}
                        style={{ width: 'auto', fontSize: 11.5, padding: '2px 4px' }}>
                        {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                      </select>
                    </td>
                    {(() => { const { sK, eK } = taskKeys(t); return cols.map((k) => {
                      const active = k >= sK && k <= eK;
                      return (
                        <td key={k} style={{ padding: 0, minWidth: 30, position: 'relative', ...(k === todayK ? { background: 'var(--surface-alt)' } : {}) }}>
                          {active && (
                            <div style={{
                              background: `var(--t-${tone}-bg)`,
                              borderTop: '1px solid var(--border)',
                              borderBottom: '1px solid var(--border)',
                              borderLeft: k === sK ? `3px solid var(--t-${tone}-fg)` : 'none',
                              borderRight: k === eK ? `3px solid var(--t-${tone}-fg)` : 'none',
                              borderTopLeftRadius: k === sK ? 4 : 0,
                              borderBottomLeftRadius: k === sK ? 4 : 0,
                              borderTopRightRadius: k === eK ? 4 : 0,
                              borderBottomRightRadius: k === eK ? 4 : 0,
                              height: 22, margin: '6px 0',
                              display: 'grid', placeItems: 'center',
                              fontSize: 10, color: `var(--t-${tone}-fg)`, fontWeight: 600,
                            }}>{k === sK ? '●' : k === eK && sK !== eK ? '●' : ''}</div>
                          )}
                        </td>
                      );
                    }); })()}
                    <td><RowMenu items={[
                      { label: 'Edit task', onClick: () => editTask(t) },
                      { label: 'Mark completed', onClick: () => { updateTimelineStatus(t.id, 'completed'); toast('Marked completed · dashboard refreshed'); }, disabled: t.status === 'completed' },
                      { label: 'Mark in progress', onClick: () => { updateTimelineStatus(t.id, 'in-progress'); toast('Marked in progress'); }, disabled: t.status === 'in-progress' },
                      { label: 'Delete task', danger: true, onClick: () => { if (window.confirm(`Delete task "${t.name}"? This cannot be undone.`)) { deleteTimeline(t.id); toast('Task deleted'); } } },
                    ]} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {addOpen && <AddTimelineModal projectId={p.id} onClose={() => setAddOpen(false)} onSave={(item: any) => { addTimelineItem(item); setAddOpen(false); toast('Added · appears on dashboard now'); }} />}
    </div>
  );
}

/** Add 7×n days to an ISO date (shared by the Add/Edit phase modals). */
const addDaysIso = (iso: string, n: number) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso; // cleared/partial date input — don't throw
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const weeksOf = (a: string, b: string) => Math.max(1, Math.round((new Date(b).getTime() - new Date(a).getTime()) / (7 * 86400000)) || 1);

/**
 * The "Responsible" cell on the timeline tab. Shows each person + % split, an
 * "added after the fact" badge for anyone the leader attached after the task's
 * period had already ended, and a compact 📝 note chip that pops the full note
 * open on click (kept small so the column stays tidy). Falls back to a clear
 * "⚠ Unassigned" when nobody is responsible yet (the default timeline no longer
 * auto-dumps role-less tasks on the PM/admin).
 */
function ResponsibleCell({ t }: { t: any }) {
  const D = useData();
  const [openNote, setOpenNote] = useState<number | null>(null);
  const realSplits = (t.splits || []).filter((s: any) => s.user);
  if (realSplits.length === 0) {
    const u = t.user ? D.team.find((x) => x.id === t.user) : null;
    if (u) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Avatar user={u} size="sm" />{u.name}</span>;
    return <span style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>⚠ Unassigned</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {realSplits.map((s: any, si: number) => {
        const su = D.team.find((x) => x.id === s.user);
        return (
          <span key={si} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, position: 'relative' }} title={`${su?.name || s.user} · ${s.pct}%`}>
            <Avatar user={su} size="sm" />{(su?.name || '').split(' ')[0]} <span className="mono" style={{ color: 'var(--text-3)' }}>{s.pct}%</span>
            {s.addedAt && (
              <span style={{ color: 'var(--accent)', cursor: 'help' }} title={`Added ${fmtShortDate(s.addedAt)} · after the task period had already ended`}>⏱</span>
            )}
            {s.note && (
              <button
                type="button"
                className="btn ghost icon sm"
                style={{ padding: 0, width: 16, height: 16, lineHeight: 1 }}
                title="Show note"
                onClick={(e) => { e.stopPropagation(); setOpenNote(openNote === si ? null : si); }}
              >📝</button>
            )}
            {openNote === si && s.note && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ position: 'absolute', top: '110%', left: 0, zIndex: 30, minWidth: 180, maxWidth: 280, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', boxShadow: '0 6px 20px rgba(0,0,0,.25)', fontSize: 11.5, color: 'var(--text-1)', whiteSpace: 'normal', fontWeight: 400 }}
              >
                {s.addedAt && <div style={{ fontSize: 10, color: 'var(--accent)', marginBottom: 4 }}>⏱ added {fmtShortDate(s.addedAt)} · after the fact</div>}
                {s.note}
                <div style={{ textAlign: 'right', marginTop: 6 }}>
                  <button type="button" className="btn ghost xs" onClick={(e) => { e.stopPropagation(); setOpenNote(null); }}>Close</button>
                </div>
              </div>
            )}
          </span>
        );
      })}
    </div>
  );
}

function AddTimelineModal({ projectId, onClose, onSave }: any) {
  const D = useData();
  // Suggest start = where this project's LAST phase ends (chained), so the
  // user doesn't re-click dates; duration (weeks) auto-fills the end date.
  const rows = D.timeline.filter((t: any) => t.project === projectId)
    .slice().sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
  const lastEnd = rows.length ? taskDates(rows[rows.length - 1]).endDate : '';
  const suggestedStart = /^\d{4}-\d{2}-\d{2}/.test(String(lastEnd || '')) ? String(lastEnd).slice(0, 10) : `${HERO_YEAR}-04-15`;
  const [form, setForm] = useState<any>({
    project: projectId, name: '', category: 'Protocol', user: D.team[0].id,
    rowType: 'task',
    durationWeeks: 2,
    // order: append at the end — without it the row sorts differently on
    // SQLite (NULLs first) vs Postgres (NULLs last) and skews the status sync.
    order: rows.length,
    startDate: suggestedStart, endDate: addDaysIso(suggestedStart, 14), status: 'planned',
  });
  const isPhaseSel = form.rowType === 'phase';
  const upd = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  // Chained date logic: start keeps the duration (end follows); duration drives
  // the end; editing the end recomputes the duration.
  const setStart = (v: string) => setForm((f: any) => ({ ...f, startDate: v, endDate: addDaysIso(v, (f.durationWeeks || 2) * 7) }));
  const setWeeks = (v: any) => setForm((f: any) => { const w = Math.max(1, Math.round(+v) || 1); return { ...f, durationWeeks: w, endDate: addDaysIso(f.startDate, w * 7) }; });
  const setEnd = (v: string) => setForm((f: any) => {
    // Reject an end typed before the start (keyboard bypasses the picker min).
    if (/^\d{4}-\d{2}-\d{2}/.test(v) && new Date(v) < new Date(f.startDate)) return f;
    return { ...f, endDate: v, durationWeeks: weeksOf(f.startDate, v) };
  });
  const start = monthFromIso(form.startDate);
  const end = monthFromIso(form.endDate);
  const days = (new Date(form.endDate).getTime() - new Date(form.startDate).getTime()) / 86400000 + 1;
  return (
    <Modal title="Add phase / task" onClose={onClose}
      footer={(
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!form.name || (!isPhaseSel && !(days >= 1))} onClick={() => onSave({ ...form, user: isPhaseSel ? '' : form.user, start, end, id: 't' + Math.random().toString(36).slice(2, 8) })}>
            <Ico.plus /> Add to timeline
          </button>
        </>
      )}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Row type</label>
          <select value={form.rowType} onChange={(e) => upd('rowType', e.target.value)}>
            <option value="task">Task (has owner &amp; compensation)</option>
            <option value="milestone">Milestone (timeline-only marker)</option>
            <option value="phase">Phase divider (groups the rows below)</option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>{isPhaseSel ? 'Phase name' : 'Phase / task name'}</label>
          <input value={form.name} onChange={(e) => upd('name', e.target.value)} placeholder={isPhaseSel ? 'e.g. Phase 2 — Protocol & Ethics' : 'e.g. Sample size re-estimation'} autoFocus />
        </div>
        {!isPhaseSel && <>
        <div className="field">
          <label>Phase category</label>
          <select value={form.category} onChange={(e) => upd('category', e.target.value)}>
            {Object.keys(D.categories).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {form.rowType !== 'milestone' && (
        <div className="field">
          <label>Responsible team member</label>
          <select value={form.user} onChange={(e) => upd('user', e.target.value)}>
            {D.team.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>
        )}
        <div className="field">
          <label>Start date</label>
          <input type="date" value={form.startDate} onChange={(e) => setStart(e.target.value)} title="Suggested to continue from the previous phase — moving it keeps the duration" />
        </div>
        <div className="field">
          <label>Duration (weeks)</label>
          <input type="number" min="1" className="tabular mono" value={form.durationWeeks} onChange={(e) => setWeeks(e.target.value)} title="The end date follows automatically" />
        </div>
        <div className="field">
          <label>End date (auto from duration)</label>
          <input type="date" value={form.endDate} min={form.startDate} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <div className="field">
          <label>Status</label>
          <select value={form.status} onChange={(e) => upd('status', e.target.value)}>
            {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
        </div>
        </>}
      </div>
      <div style={{ marginTop: 12, padding: 10, background: 'var(--surface-alt)', borderRadius: 6, fontSize: 11.5, color: 'var(--text-2)' }}>
        {isPhaseSel ? (
          <>▸ A <strong>phase divider</strong> is a visual section header. It carries no owner, dates or compensation and is excluded from the status &amp; workload calculations.</>
        ) : (<>
          💡 {rows.length ? <>Start follows the previous phase (<strong>{rows[rows.length - 1].name}</strong> ends {fmtShortDate(String(lastEnd))}). </> : null}
          Duration: <strong>{Math.max(1, Math.round(days))} day{days === 1 ? '' : 's'}</strong> ({fmtShortDate(form.startDate)} → {fmtShortDate(form.endDate)}) · Spans <strong>{end - start + 1}</strong> month{end - start === 0 ? '' : 's'} on the People×Month grid.
        </>)}
      </div>
    </Modal>
  );
}

export function EditTimelineModal({ task, onClose, onSave, onDelete }: any) {
  const D = useData();
  const { updateTimeline } = useDataActions();
  const toast = useToast();
  const td0 = taskDates(task);
  const isMilestone = task.rowType === 'milestone';
  const isPhase = task.rowType === 'phase';
  const initSplits = Array.isArray(task.splits) && task.splits.length
    ? task.splits.map((s: any) => ({ user: s.user, pct: +s.pct || 0, note: s.note ?? '', addedAt: s.addedAt }))
    : task.user
      ? [{ user: task.user, pct: 100, note: '', addedAt: undefined }]
      : []; // unassigned task — start with no people, the leader adds them
  const [form, setForm] = useState<any>({ ...task, startDate: td0.startDate, endDate: td0.endDate, splits: initSplits });
  const upd = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const splits = form.splits || [];
  const sumPct = splits.reduce((s: number, x: any) => s + (+x.pct || 0), 0);
  const updSplit = (i: number, k: string, v: any) => setForm((f: any) => ({ ...f, splits: f.splits.map((s: any, idx: number) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addSplit = () => setForm((f: any) => ({ ...f, splits: [...f.splits, { user: D.team.find((u: any) => !f.splits.some((s: any) => s.user === u.id))?.id || D.team[0].id, pct: 0 }] }));
  const rmSplit = (i: number) => setForm((f: any) => ({ ...f, splits: f.splits.length > 1 ? f.splits.filter((_: any, idx: number) => idx !== i) : f.splits }));
  const evenSplit = () => setForm((f: any) => { const n = f.splits.length; const each = Math.floor(100 / n); const rem = 100 - each * n; return { ...f, splits: f.splits.map((s: any, i: number) => ({ ...s, pct: each + (i === 0 ? rem : 0) })) }; });
  const handleSave = async () => {
    const start = monthFromIso(form.startDate);
    const end = monthFromIso(form.endDate);
    // Keep note/addedAt on each split (the server re-derives addedAt, but the
    // note is user-owned and must round-trip). Drop empty notes to stay tidy.
    const cleanSplits = (form.splits || [])
      .filter((s: any) => s.user)
      .map((s: any) => ({
        user: s.user,
        pct: +s.pct || 0,
        ...(s.note && s.note.trim() ? { note: s.note.trim() } : { note: '' }),
        ...(s.addedAt ? { addedAt: s.addedAt } : {}),
      }));

    // Auto-chain downstream (senior request): when this task's dates move, shift
    // every LATER task (in display order, incl. milestones) so each starts where
    // the previous ends, keeping its own duration. splits omitted on the cascade
    // so the server's "added after the fact" re-stamp never fires on a mere shift.
    const datesChanged = form.startDate !== td0.startDate || form.endDate !== td0.endDate;
    const shifted: any[] = [];
    // Only cascade from a WELL-FORMED end date (a blank/NaN cursor would push
    // every downstream task to invalid dates and 400 N times).
    if (datesChanged && form.project === task.project && /^\d{4}-\d{2}-\d{2}$/.test(form.endDate)) {
      // Sort by the canonical `order` field (the rest of the app trusts it) so
      // 'downstream' means downstream in order, not raw store-array position.
      const siblings = D.timeline.filter((t) => t.project === task.project)
        .slice().sort((a, b) => (((a as any).order ?? 0) - ((b as any).order ?? 0)));
      const idx = siblings.findIndex((t) => t.id === task.id);
      if (idx >= 0) {
        let cursor = form.endDate;
        for (const t of siblings.slice(idx + 1)) {
          const td = taskDates(t);
          // A point milestone / phase divider keeps its 0-week duration (no 1-week inflation).
          const w = ((t as any).rowType === 'milestone' || (t as any).rowType === 'phase')
            ? ((t as any).durationWeeks ?? 0)
            : ((t as any).durationWeeks || weeksOf(td.startDate, td.endDate));
          const sDate = cursor;
          const eDate = addDaysIso(cursor, w * 7);
          cursor = eDate;
          shifted.push({ ...t, splits: undefined, durationWeeks: w, startDate: sDate, endDate: eDate, start: monthFromIso(sDate), end: monthFromIso(eDate) });
        }
      }
    }

    await onSave({ ...form, start, end, splits: cleanSplits, user: cleanSplits[0]?.user || '' });
    for (const s of shifted) await updateTimeline(s);
    if (shifted.length) toast(`${shifted.length} following task${shifted.length === 1 ? '' : 's'} shifted`);
    onClose();
  };
  return (
    <Modal title="Edit timeline item" onClose={onClose}
      footer={(
        <>
          <button className="btn ghost" onClick={() => { onDelete(task.id); onClose(); }} style={{ marginRight: 'auto', color: 'var(--accent)' }}><Ico.trash /> Delete</button>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => void handleSave()}><Ico.check /> Save changes</button>
        </>
      )}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Phase / task name</label>
          <input value={form.name} onChange={(e) => upd('name', e.target.value)} />
        </div>
        <div className="field">
          <label>Project</label>
          <select value={form.project} onChange={(e) => upd('project', e.target.value)}>
            {D.projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.shortTitle}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Phase category</label>
          <select value={form.category} onChange={(e) => upd('category', e.target.value)}>
            {Object.keys(D.categories).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Status</label>
          <select value={form.status} onChange={(e) => upd('status', e.target.value)}>
            {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
        </div>
        {!isPhase && <>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Responsible — % split{isMilestone ? ' (milestone: optional)' : ''}</span>
            <span style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="btn ghost xs" onClick={evenSplit}>Even</button>
              <button type="button" className="btn ghost xs" onClick={addSplit} disabled={splits.length >= D.team.length}><Ico.plus /> Person</button>
            </span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {splits.length === 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', padding: '4px 0' }}>
                ⚠ Unassigned — add the person responsible for this task.
              </div>
            )}
            {splits.map((s: any, i: number) => {
              const u = D.team.find((x) => x.id === s.user);
              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 70px 18px 28px', gap: 6, alignItems: 'center' }}>
                    <Avatar user={u} size="sm" />
                    <select value={s.user} onChange={(e) => updSplit(i, 'user', e.target.value)}>
                      {D.team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <input type="number" min="0" max="100" className="tabular mono" style={{ textAlign: 'right' }} value={s.pct} onChange={(e) => updSplit(i, 'pct', +e.target.value || 0)} />
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>%</span>
                    <button type="button" className="btn ghost icon sm" onClick={() => rmSplit(i)} disabled={splits.length <= 1}><Ico.close /></button>
                  </div>
                  {s.addedAt && (
                    <div style={{ fontSize: 10.5, color: 'var(--accent)', marginLeft: 30, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      title={`This person was added on ${fmtShortDate(s.addedAt)}, after the task period had already ended.`}>
                      ⏱ added {fmtShortDate(s.addedAt)} · after the fact
                    </div>
                  )}
                  <input
                    value={s.note ?? ''}
                    onChange={(e) => updSplit(i, 'note', e.target.value)}
                    placeholder="Note (optional) — e.g. why this person was added later"
                    style={{ marginLeft: 30, fontSize: 11, padding: '3px 6px' }}
                  />
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: sumPct === 100 || splits.length === 0 ? 'var(--text-3)' : 'var(--accent)', fontWeight: sumPct === 100 || splits.length === 0 ? 400 : 600 }}>
            {splits.length === 0
              ? 'No responsible person yet — this task will show as Unassigned.'
              : sumPct === 100 ? '✓ Splits total 100% — links to each person’s compensation' : `⚠ Splits total ${sumPct}% — should equal 100%`}
          </div>
        </div>
        <div className="field">
          <label>Start date</label>
          <input type="date" value={form.startDate} title="Moving the start keeps the duration and shifts the end" onChange={(e) => {
            const v = e.target.value;
            setForm((f: any) => { const w = f.durationWeeks || weeksOf(f.startDate, f.endDate); return { ...f, startDate: v, durationWeeks: w, endDate: addDaysIso(v, w * 7) }; });
          }} />
        </div>
        <div className="field">
          <label>Duration (weeks)</label>
          <input type="number" min="1" className="tabular mono" value={form.durationWeeks ?? weeksOf(form.startDate, form.endDate)} title="The end date follows automatically" onChange={(e) => {
            const w = Math.max(1, Math.round(+e.target.value) || 1);
            setForm((f: any) => ({ ...f, durationWeeks: w, endDate: addDaysIso(f.startDate, w * 7) }));
          }} />
        </div>
        <div className="field">
          <label>End date (auto from duration)</label>
          <input type="date" value={form.endDate} min={form.startDate} onChange={(e) => {
            const v = e.target.value;
            setForm((f: any) => {
              if (/^\d{4}-\d{2}-\d{2}/.test(v) && new Date(v) < new Date(f.startDate)) return f;
              return { ...f, endDate: v, durationWeeks: weeksOf(f.startDate, v) };
            });
          }} />
        </div>
        </>}
        {isPhase && (
          <div className="field" style={{ gridColumn: 'span 2', fontSize: 11.5, color: 'var(--text-2)' }}>
            ▸ Phase divider — a visual section header only. It has no owner, dates or compensation.
          </div>
        )}
      </div>
    </Modal>
  );
}

function ProjectSettings({ p }: { p: any }) {
  const D = useData();
  const { updateProject } = useDataActions();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState<any>({
    title: p.title || '', shortTitle: p.shortTitle || '', design: p.design || '',
    area: p.area || '', sponsor: p.sponsor || '', pi: p.pi || '',
    statusOverride: p.statusOverride || '',
    piName: p.piName || '', coIsName: (p.coIsName || []).join(', '),
    sampleSize: p.sampleSize || 0, ethics: p.ethics || '', registry: p.registry || '',
    redcap: p.redcap || '', startDate: p.startDate || '', endDate: p.endDate || '',
  });
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await updateProject(p.id, {
        title: f.title, shortTitle: f.shortTitle, design: f.design, area: f.area,
        sponsor: f.sponsor, pi: f.pi, piName: f.piName,
        // Status is auto-synced from the timeline; only the manual pin is sent
        // (empty = auto → clears any override server-side).
        statusOverride: f.statusOverride || null,
        coIsName: f.coIsName ? String(f.coIsName).split(',').map((s: string) => s.trim()).filter(Boolean) : [],
        sampleSize: Number(f.sampleSize) || 0, ethics: f.ethics, registry: f.registry,
        redcap: f.redcap || undefined, startDate: f.startDate, endDate: f.endDate,
      } as any);
      toast('Project saved');
    } catch { /* error toast shown by action */ }
    finally { setBusy(false); }
  };
  const archive = async () => {
    if (!window.confirm(`Archive ${p.code}? It will be set to "Terminated".`)) return;
    try { await updateProject(p.id, { statusOverride: 'Terminated' } as any); toast('Project archived'); }
    catch { /* shown */ }
  };
  return (
    <div className="card">
      <div className="card-h">
        <div>
          <h3>Project settings</h3>
          <div className="sub">Edit profile fields. Changes save to the server.</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn primary sm" onClick={() => void save()} disabled={busy || !f.title}><Ico.check /> {busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
      <div className="card-b">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label>Full title</label>
            <input value={f.title} onChange={set('title')} />
          </div>
          <div className="field"><label>Project code</label><input className="mono" value={p.code} disabled /></div>
          <div className="field"><label>Short title</label><input value={f.shortTitle} onChange={set('shortTitle')} /></div>
          <div className="field"><label>Study design</label><input value={f.design} onChange={set('design')} /></div>
          <div className="field"><label>Clinical area</label><input value={f.area} onChange={set('area')} /></div>
          <div className="field"><label>Sponsor / client</label><input value={f.sponsor} onChange={set('sponsor')} /></div>
          <div className="field"><label>Project Manager (in system)</label>
            <select value={f.pi} onChange={set('pi')}>{D.team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
          </div>
          <div className="field"><label>Status {f.statusOverride
            ? <span className="chip" data-tone="amber" style={{ marginLeft: 6 }}><span className="dot" />Manual override</span>
            : <span className="chip" data-tone="green" style={{ marginLeft: 6 }}><span className="dot" />Auto · from timeline</span>}</label>
            <select value={f.statusOverride} onChange={set('statusOverride')}>
              <option value="">Auto — follows the timeline (now: {p.status})</option>
              {PROJECT_STATUS_OPTIONS.map((s) => <option key={s} value={s}>Override: {s}</option>)}
            </select>
            <div style={{ fontSize: 10.5, color: 'var(--text-3)', marginTop: 4 }}>
              Status tracks today’s position in the project timeline automatically. Pick an override only for decisions the timeline can’t know (On hold, Terminated, closing early).
            </div>
          </div>
          <div className="field"><label>Principal Investigator (documentation)</label><input value={f.piName} onChange={set('piName')} placeholder="Prof. ____, MD" /></div>
          <div className="field"><label>Co-Investigators (documentation)</label><input value={f.coIsName} onChange={set('coIsName')} placeholder="Name 1, Name 2" /></div>
          <div className="field"><label>Sample size</label><input type="number" value={f.sampleSize} onChange={set('sampleSize')} /></div>
          <div className="field"><label>Ethics approval</label><input value={f.ethics} onChange={set('ethics')} /></div>
          <div className="field"><label>Trial registration #</label><input className="mono" value={f.registry} onChange={set('registry')} /></div>
          <div className="field"><label>REDCap project link</label><input className="mono" value={f.redcap} onChange={set('redcap')} placeholder="https://redcap.cmu.ac.th/redcap_v…" /></div>
          <div className="field"><label>Start date</label><input type="date" value={f.startDate} onChange={set('startDate')} /></div>
          <div className="field"><label>Expected end date</label><input type="date" value={f.endDate} onChange={set('endDate')} /></div>
        </div>
        <hr className="rule" />
        <div style={{ display: 'flex', gap: 6, fontSize: 11.5, color: 'var(--text-2)' }}>
          <button className="btn ghost sm" style={{ color: 'var(--accent)' }} onClick={() => void archive()}><Ico.trash /> Archive project</button>
        </div>
      </div>
    </div>
  );
}

/** Modal to edit a project's core profile fields. Patches via updateProject. */
function ProjectEditModal({ p, onClose }: { p: any; onClose: () => void }) {
  const { updateProject } = useDataActions();
  const toast = useToast();
  const [form, setForm] = useState<any>({
    title: p.title || '', shortTitle: p.shortTitle || '', sponsor: p.sponsor || '',
    area: p.area || '', design: p.design || '', statusOverride: p.statusOverride || '', sampleSize: p.sampleSize || 0,
  });
  const [busy, setBusy] = useState(false);
  const set = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await updateProject(p.id, {
        title: form.title, shortTitle: form.shortTitle, sponsor: form.sponsor,
        area: form.area, design: form.design, statusOverride: form.statusOverride || null,
        sampleSize: Number(form.sampleSize) || 0,
      } as any);
      toast('Project saved'); onClose();
    } catch { /* error toast already shown by the action */ }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Edit ${p.code}`} onClose={onClose} wide
      footer={<><button className="btn" onClick={onClose} disabled={busy}>Cancel</button><button className="btn primary" onClick={() => void save()} disabled={busy || !form.title}><Ico.check /> {busy ? 'Saving…' : 'Save'}</button></>}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field" style={{ gridColumn: 'span 2' }}><label>Project title</label><input value={form.title} onChange={set('title')} /></div>
        <div className="field"><label>Short title</label><input value={form.shortTitle} onChange={set('shortTitle')} /></div>
        <div className="field"><label>Sponsor</label><input value={form.sponsor} onChange={set('sponsor')} /></div>
        <div className="field"><label>Study design</label><input value={form.design} onChange={set('design')} /></div>
        <div className="field"><label>Area</label><input value={form.area} onChange={set('area')} /></div>
        <div className="field"><label>Status</label>
          <select value={form.statusOverride} onChange={set('statusOverride')}>
            <option value="">Auto — follows the timeline (now: {p.status})</option>
            {PROJECT_STATUS_OPTIONS.map((s) => <option key={s} value={s}>Override: {s}</option>)}
          </select>
        </div>
        <div className="field"><label>Sample size</label><input type="number" className="tabular mono" value={form.sampleSize} onChange={set('sampleSize')} /></div>
      </div>
    </Modal>
  );
}

/** Modal to add a unit member to a project. Calls addProjectMember. */
function AddMemberModal({ p, onClose }: { p: any; onClose: () => void }) {
  const D = useData();
  const { addProjectMember } = useDataActions();
  const toast = useToast();
  const candidates = D.team.filter((u) => !p.members.includes(u.id));
  const [userId, setUserId] = useState<string>(candidates[0]?.id || '');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!userId) return;
    setBusy(true);
    try { await addProjectMember(p.id, userId); toast('Member added'); onClose(); }
    catch { /* error toast already shown */ }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Add member to ${p.code}`} onClose={onClose}
      footer={<><button className="btn" onClick={onClose} disabled={busy}>Cancel</button><button className="btn primary" onClick={() => void add()} disabled={busy || !userId}><Ico.check /> {busy ? 'Adding…' : 'Add member'}</button></>}>
      {candidates.length === 0 ? (
        <div style={{ color: 'var(--text-2)', fontSize: 12.5 }}>Everyone on the unit roster is already a member of this project.</div>
      ) : (
        <div className="field"><label>Member</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}>
            {candidates.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}
          </select>
        </div>
      )}
    </Modal>
  );
}

function ProjectSummary({ p }: { p: any }) {
  const D = useData();
  const { removeProjectMember } = useDataActions();
  const { open: openProfile } = useProfileViewer();
  const toast = useToast();
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [editProjOpen, setEditProjOpen] = useState(false);
  const pi = D.team.find((u) => u.id === p.pi);
  const members = p.members.map((id: string) => D.team.find((u) => u.id === id));
  const tasks = D.timeline.filter((t) => t.project === p.id && (t as any).rowType !== 'phase');
  const inProgress = tasks.filter((t) => t.status === 'in-progress').length;
  const completed = tasks.filter((t) => t.status === 'completed').length;
  const planned = tasks.filter((t) => t.status === 'planned').length;
  const delayed = tasks.filter((t) => t.status === 'delayed').length;
  const phasePctNum = tasks.length ? (completed / tasks.length * 100) : 0;
  const phasePct = phasePctNum.toFixed(2);
  // Calendar progress: (today − start) / (end − start), clamped 0–100. Shown
  // as a second bar so phase-% can be read against how much of the project's
  // time window is already gone.
  const datePctNum = (() => {
    const s = new Date(p.startDate).getTime();
    const e = new Date(p.endDate).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;
    return Math.max(0, Math.min(100, ((Date.now() - s) / (e - s)) * 100));
  })();
  // Schedule chip: phase % is step-wise (0 until a whole phase completes) while
  // calendar % is continuous, so compare against EFFECTIVE progress (in-progress
  // phases count half) with a 10-point grace band — a healthy young project
  // whose first phase is still running shouldn't be flagged "behind" on day one.
  const effPctNum = tasks.length ? ((completed + 0.5 * inProgress) / tasks.length) * 100 : 0;
  const behindSchedule = datePctNum != null && tasks.length > 0 && datePctNum - effPctNum > 10;
  const isCohort = p.design.toLowerCase().includes('cohort');
  const logs = D.logs.filter((l) => l.project === p.id).slice(0, 6);
  // Phases whose REAL date window covers today (not the legacy month index).
  const todayIso = new Date().toISOString().slice(0, 10);
  const activeNow = D.timeline.filter((t) => {
    if (t.project !== p.id) return false;
    if (t.status === 'completed') return false; // a finished phase isn't "active"
    const td = taskDates(t);
    return td.startDate <= todayIso && todayIso <= td.endDate;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-h"><h3>Design specification</h3><button className="btn ghost sm" onClick={() => setEditProjOpen(true)}><Ico.edit /> Edit</button></div>
          <div className="card-b">
            <dl className="kv">
              <dt>Design type</dt><dd>{p.design}</dd>
              <dt>Number of arms</dt><dd>{isCohort ? '—' : '2'}</dd>
              <dt>Intervention</dt><dd>{p.code === 'HERO-001' ? 'Generic febuxostat 80 mg/d' : 'See protocol §4'}</dd>
              <dt>Comparator</dt><dd>{p.code === 'HERO-001' ? 'Branded febuxostat 80 mg/d' : 'See protocol §4'}</dd>
              <dt>Randomisation</dt><dd>{isCohort ? 'Not applicable' : 'Yes — 1:1 stratified'}</dd>
              <dt>Blinding</dt><dd>{isCohort ? 'Not applicable' : 'Double-blind'}</dd>
              <dt>Sample size</dt><dd className="tabular">{p.sampleSize.toLocaleString()}</dd>
              <dt>Recruitment duration</dt><dd>52 weeks</dd>
              <dt>Treatment duration</dt><dd>26 weeks</dd>
              <dt>Follow-up duration</dt><dd>13 weeks</dd>
              {(() => {
                const outs = (p.outcomes || []) as any[];
                if (outs.length === 0) return <><dt>Primary outcome</dt><dd>Serum uric acid at 24 wk</dd></>;
                const primary = outs.find((o) => o.kind === 'Primary');
                const secondary = outs.filter((o) => o.kind === 'Secondary');
                return (
                  <>
                    <dt>Primary outcome</dt>
                    <dd>{primary ? `${primary.name}${primary.timepointWeeks != null ? ` (wk ${primary.timepointWeeks})` : ''}` : '—'}</dd>
                    {secondary.length > 0 && (
                      <>
                        <dt>Secondary outcome{secondary.length > 1 ? 's' : ''}</dt>
                        <dd>{secondary.map((o) => o.name).filter(Boolean).join('; ') || '—'}</dd>
                      </>
                    )}
                  </>
                );
              })()}
              <dt>Data collection</dt><dd>REDCap · paper CRF backup</dd>
            </dl>
          </div>
        </div>

        <div className="card">
          <div className="card-h"><h3>Project profile</h3></div>
          <div className="card-b">
            <dl className="kv">
              <dt>Code</dt><dd className="mono">{p.code}</dd>
              <dt>Status</dt><dd><span className="chip" data-tone="blue"><span className="dot" />{p.status}</span></dd>
              <dt>Clinical area</dt><dd>{p.area}</dd>
              <dt>Sponsor</dt><dd>{p.sponsor}</dd>
              <dt>Project Manager</dt><dd>{pi?.name} <span style={{ color: 'var(--text-2)', fontSize: 11 }}>(in system)</span></dd>
              <dt>Principal Investigator</dt><dd>{p.piName || '—'} <span style={{ color: 'var(--text-3)', fontSize: 11 }}>(documentation only)</span></dd>
              {p.coIsName && p.coIsName.length > 0 && <><dt>Co-Investigators</dt><dd>{p.coIsName.join(', ')}</dd></>}
              <dt>Ethics</dt><dd>{p.ethics}</dd>
              <dt>Registry</dt><dd className="mono">{p.registry}</dd>
              <dt>Start</dt><dd>{p.startDate}</dd>
              <dt>Expected end</dt><dd>{p.endDate}</dd>
            </dl>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-h"><h3>Project progression</h3><span className="chip" data-tone="slate"><span className="dot" />{tasks.length} phases planned</span></div>
          <div className="card-b">
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <div className="v tabular" style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em' }}>{phasePct}%</div>
                <div className="sub">{completed} of {tasks.length} phases completed</div>
              </div>
              <div style={{ display: 'flex', gap: 8, fontSize: 11.5, color: 'var(--text-2)' }}>
                <span><span className="status-dot in-progress" />{inProgress} in progress</span>
                <span><span className="status-dot planned" />{planned} planned</span>
                {delayed > 0 && <span><span className="status-dot delayed" />{delayed} delayed</span>}
              </div>
            </div>
            <div className="bar-track" style={{ height: 10, marginBottom: 14 }}>
              <div className="bar-fill" style={{ width: phasePct + '%' }} />
            </div>

            {datePctNum != null && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
                    Time elapsed · <span className="v tabular" style={{ fontWeight: 700, color: 'var(--text)' }}>{datePctNum.toFixed(2)}%</span> of the project window
                  </div>
                  {tasks.length > 0 && (
                    <span className="chip" data-tone={behindSchedule ? 'amber' : 'green'} title={`Work ${effPctNum.toFixed(0)}% (in-progress phases counted half) vs time ${datePctNum.toFixed(0)}%`}>
                      <span className="dot" />{behindSchedule ? 'Behind schedule' : 'On schedule'}
                    </span>
                  )}
                </div>
                <div className="bar-track" style={{ height: 10 }}>
                  <div className="bar-fill" style={{ width: datePctNum.toFixed(2) + '%', background: 'var(--navy)' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-3)', marginTop: 3 }}>
                  <span className="mono">{p.startDate}</span>
                  <span>start → deadline</span>
                  <span className="mono">{p.endDate}</span>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {[
                { l: 'In progress', v: inProgress, t: 'var(--t-blue-fg)' },
                { l: 'Completed', v: completed, t: 'var(--t-green-fg)' },
                { l: 'Planned', v: planned, t: 'var(--text-2)' },
                { l: 'Delayed', v: delayed, t: 'var(--accent)' },
              ].map((s) => (
                <div key={s.l} style={{ background: 'var(--surface-alt)', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.l}</div>
                  <div className="v tabular" style={{ fontSize: 18, fontWeight: 700, color: s.t, marginTop: 2 }}>{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-h"><h3>Team — {members.length}</h3><button className="btn ghost sm" onClick={() => setAddMemberOpen(true)}><Ico.plus /> Add</button></div>
          <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {members.map((m: any) => {
              if (!m) return null;
              const role = m.id === p.pi ? 'Project Manager' : 'Project member';
              return (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 4 }}>
                  <Avatar user={m} size="sm" />
                  <button type="button" onClick={() => openProfile(m.id)} title="View CV, certificates & signature" style={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--accent)' }}>{m.name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{m.role}</div>
                  </button>
                  <span className="chip" data-tone={m.id === p.pi ? 'red' : 'slate'}><span className="dot" />{role}</span>
                  {m.id !== p.pi && (
                    <button className="btn ghost icon sm" title="Remove from project" onClick={() => { if (window.confirm(`Remove ${m.name} from ${p.code}?`)) { void removeProjectMember(p.id, m.id).then(() => toast('Member removed')).catch(() => undefined); } }}><Ico.close /></button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-h"><h3>Recent activity</h3><button className="btn ghost sm" onClick={() => { if (logs.length) downloadCsv(logs as any[], { When: (l: any) => l.at ?? l.when ?? l.date ?? '', Who: (l: any) => l.by ?? l.user ?? '', Action: (l: any) => l.text ?? l.action ?? l.message ?? l.detail ?? '' }, `${p.code}-activity-${dateStamp()}.csv`); }}><Ico.dl /> Export logs</button></div>
          <div className="card-b flush">
            {logs.length === 0 && <div style={{ padding: 20, color: 'var(--text-2)', fontSize: 12, textAlign: 'center' }}>No activity logged yet.</div>}
            {logs.map((l) => (
              <div key={l.id} style={{ padding: '10px 14px', display: 'grid', gridTemplateColumns: '110px 1fr 100px', alignItems: 'flex-start', gap: 10, borderBottom: '1px solid var(--border)' }}>
                <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{l.at}</span>
                <div>
                  <span className="chip" data-tone={l.type.includes('submitted') ? 'green' : l.type.includes('Issue') ? 'red' : 'slate'} style={{ marginRight: 6 }}><span className="dot" />{l.type}</span>
                  <span style={{ fontSize: 12 }}>{l.msg}</span>
                </div>
                <span style={{ fontSize: 11 }}><UserBy id={l.user} /></span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-h"><h3>Active timeline (now)</h3><span className="chip" data-tone="slate"><span className="dot" />{activeNow.length} phase{activeNow.length === 1 ? '' : 's'}</span></div>
          <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activeNow.length === 0 && <div style={{ color: 'var(--text-2)', fontSize: 12, textAlign: 'center', padding: 20 }}>No phases active today.</div>}
            {activeNow.map((t) => {
              const tone = (D.categories[t.category] || { tone: 'slate' }).tone;
              const u = D.team.find((x) => x.id === t.user);
              return (
                <div key={t.id} className="taskcard" data-tone={tone} style={{ position: 'static', cursor: 'default' }}>
                  <div className="code">{p.code} · {t.category}</div>
                  <div className="nm">{t.name}</div>
                  <div className="meta">
                    <span className={'status-dot ' + t.status} />
                    <span style={{ fontSize: 10.5, color: 'var(--text-2)' }}>{STATUS_META[t.status].label}</span>
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Avatar user={u} size="sm" /> <span style={{ fontSize: 10.5 }}>{u?.initials}</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {addMemberOpen && <AddMemberModal p={p} onClose={() => setAddMemberOpen(false)} />}
      {editProjOpen && <ProjectEditModal p={p} onClose={() => setEditProjOpen(false)} />}
    </div>
  );
}
